import React from 'react';
import { Grid, HeaderData } from './layouts/Grid';
import { RadioButton } from './RadioButton';
import _ from 'lodash';
import UUID from '@ax2/common/model/UUID';
import {
  ParticipantDefinedField,
  ParticipantDefinedFieldData,
} from './ParticipantDefinedOption';
import { MobileGrid } from './layouts/MobileGrid';
import { RadioButtonsList } from './RadioButtonsList';
import QuestionType from '@ax2/common/model/QuestionType';

export interface RadioButtonsGridColOption {
  id: UUID;
  title: string;
  subtitle?: string;
}
export interface RadioButtonsGridRowOption {
  id: UUID;
  title: string;
  subtitle?: string;
}
export interface RadioButtonsGridOption {
  colId: UUID;
  rowId: UUID;
  selected: boolean;
}

export type RadioButtonsGridParticipantDefinedOption = ParticipantDefinedFieldData;

export interface RadioButtonsGridData {
  cols: RadioButtonsGridColOption[];
  rows: (
    | RadioButtonsGridRowOption
    | RadioButtonsGridParticipantDefinedOption)[];
  isDesktop: boolean;
  isExclusivePerRow: boolean;
  preselected?: RadioButtonsGridOption[];
  onCompletion: (opts: RadioButtonsGridOption[]) => void;
  key?: string;
  questionType?: QuestionType;
}

function getInitialState(
  data: RadioButtonsGridData
): RadioButtonsGridOption[][] {
  const state = _.map(data.cols, col => {
    return _.map(data.rows, row => {
      const preOpt = _.find(
        data.preselected,
        p => p.colId === col.id && p.rowId === row.id
      );
      const opt: RadioButtonsGridOption = preOpt
        ? preOpt
        : {
            colId: col.id,
            rowId: row.id,
            selected: false,
          };
      return opt;
    });
  });
  return state;
}
function getNormalizedData(
  data: RadioButtonsGridOption[][]
): RadioButtonsGridOption[] {
  return _.filter(_.flatten(data), d => !!d.selected);
}

export const RadioButtonsGrid: React.FC<RadioButtonsGridData> = data => {
  return data.isDesktop ? (
    <RadioButtonsGridDesktop {...data} />
  ) : (
    <RadioButtonsGridMobile {...data} />
  );
};

const RadioButtonsGridDesktop: React.FC<RadioButtonsGridData> = data => {
  const [selectionData, setSelectionData] = React.useState<
    RadioButtonsGridOption[][]
  >(getInitialState(data));
  const cols: HeaderData[] = data.cols.map(c => c.title);
  const rows: HeaderData[] = data.rows.map(r =>
    'title' in r ? r.title : <ParticipantDefinedField {...r} />
  );
  return (
    <Grid
      cols={cols}
      rows={rows}
      elementRenderer={(colIndex, rowIndex) => {
        const opt = selectionData[colIndex][rowIndex];

        return (
          <RadioButton
            tabIndex={1 + colIndex + rowIndex * cols.length}
            hasSelection={opt.selected}
            onCompletion={_v => {
              const newSelectionData = [...selectionData];
              //Clean prev selection

              if (data.isExclusivePerRow) {
                _.forEach(newSelectionData, d => {
                  d[rowIndex].selected = false;
                });
              } else {
                _.forEach(newSelectionData[colIndex], d => {
                  d.selected = false;
                });
              }

              //set selection
              opt.selected = true;
              setSelectionData(newSelectionData);
              data.onCompletion(getNormalizedData(newSelectionData));
            }}
          />
        );
      }}
    />
  );
};

const RadioButtonsGridMobile: React.FC<RadioButtonsGridData> = data => {
  const primary = !data.isExclusivePerRow ? data.cols : data.rows;
  const secondary = !data.isExclusivePerRow ? data.rows : data.cols;

  return (
    <MobileGrid
      primaryOptions={primary}
      secondaryRenderer={(primaryOptionId, _primaryOptionIndex) => {
        const preselectedOpt = _.find(data.preselected, s => {
          const sId = !data.isExclusivePerRow ? s.colId : s.rowId;
          return sId === primaryOptionId;
        });
        const preselectedId = preselectedOpt
          ? !data.isExclusivePerRow
            ? preselectedOpt.rowId
            : preselectedOpt.colId
          : undefined;
        return (
          <RadioButtonsList
            options={secondary}
            preselected={preselectedId}
            onCompletion={s => {
              const oldData = _.filter(data.preselected, p => {
                const sPrimaryId = !data.isExclusivePerRow ? p.colId : p.rowId;
                return sPrimaryId !== primaryOptionId;
              }); //Old selection without primary section
              const newData = !data.isExclusivePerRow
                ? {
                    colId: primaryOptionId,
                    rowId: s,
                    selected: true,
                  }
                : { colId: s, rowId: primaryOptionId, selected: true };
              data.onCompletion(_.concat(oldData, [newData]));
            }}
            key={primaryOptionId}
          />
        );
      }}
    />
  );
};
